import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { fmtH, mesLabel } from "./portalShared";

const TIPO_LABEL = { compensacion: "Compensación", pago: "Pago", ajuste: "Ajuste admin", recuperacion: "Recuperación" };

export function SaldoDesgloseDialog({ open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/portal-empleado/saldo-desglose").then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="dialog-saldo-desglose">
        <DialogHeader>
          <DialogTitle>¿Por qué tengo este saldo?</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Pendiente = saldo generado en el cierre + ajustes/recuperaciones − compensaciones − pagos. Sólo periodos cerrados por administración.
          </DialogDescription>
        </DialogHeader>
        {loading && <div className="py-8 text-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Cargando…</div>}
        {data && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-zinc-900/60 rounded p-2"><div className="text-[10px] uppercase text-zinc-500">Pendiente total</div>
                <div className={`text-lg font-semibold ${data.pendiente_total > 0 ? "text-emerald-300" : data.pendiente_total < 0 ? "text-red-300" : "text-zinc-200"}`} data-testid="desglose-pendiente-total">{fmtH(data.pendiente_total)}</div></div>
              <div className="bg-zinc-900/60 rounded p-2"><div className="text-[10px] uppercase text-zinc-500">En solicitudes</div>
                <div className="text-lg font-semibold text-amber-300">−{Number(data.reservado_en_solicitudes || 0).toFixed(1)} h</div></div>
              <div className="bg-zinc-900/60 rounded p-2"><div className="text-[10px] uppercase text-zinc-500">Disponible</div>
                <div className="text-lg font-semibold text-zinc-100" data-testid="desglose-disponible">{fmtH(data.disponible, false)}</div></div>
            </div>
            {data.detalle.length === 0 && (
              <div className="text-xs text-zinc-500 text-center py-4" data-testid="desglose-vacio">Aún no hay periodos cerrados. El saldo se consolida cuando administración cierra el mes.</div>
            )}
            {data.detalle.map((p) => (
              <div key={p.periodo} className="border border-zinc-800 rounded-lg overflow-hidden" data-testid={`desglose-${p.periodo}`}>
                <div className="px-3 py-2 bg-zinc-900/60 flex items-center justify-between text-sm">
                  <span className="capitalize text-zinc-100">{mesLabel(p.periodo)}</span>
                  <span className={`font-mono font-semibold ${p.pendiente > 0 ? "text-emerald-300" : p.pendiente < 0 ? "text-red-300" : "text-zinc-400"}`}>{fmtH(p.pendiente)} pendiente</span>
                </div>
                <div className="px-3 py-2 text-xs grid grid-cols-2 sm:grid-cols-5 gap-2">
                  <div><span className="text-zinc-500">Debía</span> <span className="font-mono">{p.esperadas} h</span></div>
                  <div><span className="text-zinc-500">Trabajó</span> <span className="font-mono">{p.trabajadas} h</span></div>
                  <div><span className="text-zinc-500">Generado</span> <span className="font-mono">{fmtH(p.saldo)}</span></div>
                  <div><span className="text-zinc-500">Compensado</span> <span className="font-mono text-amber-300">−{p.compensado} h</span></div>
                  <div><span className="text-zinc-500">Pagado</span> <span className="font-mono text-violet-300">−{p.pagado} h</span></div>
                </div>
                {p.movimientos.length > 0 && (
                  <div className="border-t border-zinc-800/60 divide-y divide-zinc-800/40">
                    {p.movimientos.map((m) => (
                      <div key={m.id} className="px-3 py-1.5 text-[11px] flex items-center gap-2">
                        <span className="font-mono text-zinc-500">{m.fecha}</span>
                        <span className="text-zinc-300">{TIPO_LABEL[m.tipo] || m.tipo}</span>
                        <span className="flex-1 text-zinc-500 truncate">{m.motivo}</span>
                        <span className={`font-mono ${m.horas < 0 ? "text-amber-300" : "text-emerald-300"}`}>{fmtH(m.horas)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {p.fuente_horario === "default" && <div className="px-3 py-1 text-[10px] text-amber-300/80">Calculado con horario por defecto (8 h/día).</div>}
              </div>
            ))}
            {data.legacy?.length > 0 && (
              <div className="text-[11px] text-zinc-500 border border-dashed border-zinc-800 rounded p-2" data-testid="desglose-legacy">
                {data.legacy.length} movimiento(s) histórico(s) sin periodo asignado (legacy, {fmtH(data.legacy_saldo_no_asignado)}): se muestran a título informativo y no cuentan en el pendiente.
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
